import { AsyncPipe, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { BehaviorSubject, filter, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';

import { ButtonComponent } from '@shared/components/button/button.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  createNoteMutation,
  EntityType,
  GqlService,
  NoteType,
  updateNoteMutation,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { QuarterCloseChecklistPeriodCloseService } from '@shared/services/quarter-close-checklist-period-close.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { QuarterCloseNotesQuery } from '@shared/store/quarter-close-notes/quarter-close-notes.query';
import {
  DirtyStep,
  MonthlyReviewNote,
  QuarterCloseNotesStore,
  TaskInstruction,
} from '@shared/store/quarter-close-notes/quarter-close-notes.store';
import { Workflow } from '@shared/store/workflow/workflow.store';

import { MonthlyReviewNotesCounterComponent } from '../../monthly-review-notes-counter/monthly-review-notes-counter.component';
import { MonthlyReviewNotesComponent } from '../monthly-review-notes/monthly-review-notes.component';
import { Notes } from '../notes.enum';

const TI_DESCRIPTION = `(Include notes or instructions for how to complete this step.
  Any notes added here will persist month-to-month.)`;
const MRN_DESCRIPTION = `(Use this to add any notes or explanation specific to this step
  for this month's close.)`;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    InputComponent,
    IconComponent,
    TooltipDirective,
    ButtonComponent,
    DatePipe,
    AsyncPipe,
    MonthlyReviewNotesComponent,
    MonthlyReviewNotesCounterComponent,
  ],
  selector: 'aux-checklist-notes-editor',
  styleUrl: './checklist-notes-editor.component.scss',
  templateUrl: './checklist-notes-editor.component.html',
})
export class ChecklistNotesEditorComponent implements AfterViewInit, OnDestroy {
  readonly periodCloseService = inject(QuarterCloseChecklistPeriodCloseService);
  readonly quarterCloseNotesQuery = inject(QuarterCloseNotesQuery);

  private readonly authQuery = inject(AuthQuery);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly gqlService = inject(GqlService);
  private readonly overlayService = inject(OverlayService);
  private readonly quarterCloseNotesStore = inject(QuarterCloseNotesStore);

  @Input() checklistAdminPermission!: boolean;
  @ViewChild(InputComponent) inputComponent!: InputComponent;
  readonly Notes = Notes;
  pending = false;
  textarea = new FormControl('');
  @Input() type: Notes = Notes.TaskInstructions;
  @Input() workflow$!: BehaviorSubject<null | Workflow>;

  private taskInstructions$!: Subscription;

  ngAfterViewInit(): void {
    this.textarea.valueChanges
      .pipe(
        filter((value) => !value && this.isMonthlyReviewNotes),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.textarea.markAsPristine());

    this.quarterCloseNotesQuery.selectedPeriod$
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(this.disableOnInit);

    this.workflow$
      .asObservable()
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe((workflow: Workflow) => {
        if (this.isTaskInstructions) {
          this.taskInstructions$?.unsubscribe();
          this.taskInstructions$ = this.quarterCloseNotesQuery
            .taskInstruction$((workflow as Workflow)?.id)
            .pipe(
              filter((content) => !!content?.text),
              distinctUntilChanged((prev, curr) => prev?.text === curr?.text),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((content) => this.textarea.setValue(content.text));
        }
      });
    this.disableOnInit();
  }

  ngOnDestroy(): void {
    this.setNotesDirtyState(false, false);
  }

  private createMonthlyReviewNote(): void {
    const nowDate = new Date();

    this.createNote$(NoteType.NOTE_TYPE_MONTHLY_REVIEW_NOTE)
      .pipe(
        tap(({ data, errors }) => {
          if (errors.length) {
            this.overlayService.error(errors);
          } else if (data) {
            this.quarterCloseNotesStore.addMonthlyReviewNote(this.workflowId, {
              created: nowDate.toString(),
              createdBy: this.authQuery.user().sub,
              id: data.id,
              text: data.message,
              workflowId: this.workflowId,
            });
          }

          this.pending = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this.onCancel);
  }

  private createNote$(
    noteType: NoteType,
    message: string = this.textarea.value as string
  ): Observable<GraphqlResponse<createNoteMutation>> {
    return this.gqlService.createNote$({
      entity_id: (this.workflow$ as BehaviorSubject<Workflow>).value.id,
      entity_type: EntityType.WF_DETAIL,
      message,
      note_type: noteType,
    });
  }

  get description(): string {
    return this.type === Notes.TaskInstructions ? TI_DESCRIPTION : MRN_DESCRIPTION;
  }

  get dirtyStepState(): DirtyStep {
    return this.quarterCloseNotesStore.getValue().notes[this.workflowId].dirty;
  }

  private disableOnInit = (): void => {
    if (this.isTaskInstructions) {
      this.textarea.disable();
    } else if (this.selectedPeriod !== this.periodCloseService.currentOpenMonth) {
      this.textarea.reset();
      this.textarea.disable();
      this.textarea.markAsPristine();
    } else {
      this.textarea.reset();
      this.textarea.enable({ emitEvent: false });
      this.textarea.markAsPristine();
    }

    this.quarterCloseNotesStore.updateDirtyState(this.workflowId, new DirtyStep(false, false));
    this.changeDetectorRef.detectChanges();
  };

  get isActionsVisible(): boolean {
    if (this.isTaskInstructions) {
      return this.textarea.enabled;
    }

    return this.textarea.dirty;
  }

  get isMonthlyReviewNotes(): boolean {
    return this.type === Notes.MonthlyReviewNotes;
  }

  get isTaskInstructions(): boolean {
    return this.type === Notes.TaskInstructions;
  }

  get monthlyReviewNotes(): MonthlyReviewNote[] | null {
    return this.quarterCloseNotesStore.getValue().notes[this.workflowId]?.monthlyReviewNotes;
  }

  noPermissionsTooltipText(): string {
    if (this.periodCloseService.currentOpenMonth !== this.selectedPeriod) {
      return 'Cannot edit for closed months.';
    }

    if (!this.checklistAdminPermission) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    return '';
  }

  onBlur(): void {
    if (!this.textarea.value && this.isMonthlyReviewNotes) {
      this.onCancel();
    }
  }

  onCancel = (): void => {
    if (this.isTaskInstructions) {
      this.textarea.setValue(this.taskInstruction?.text as string);
      this.textarea.disable();
      this.setNotesDirtyState(!!this.dirtyStepState?.isMonthlyReviewNotesDirty, false);
    }

    if (this.isMonthlyReviewNotes) {
      this.textarea.reset();
      this.textarea.markAsPristine();
      this.setNotesDirtyState(false, !!this.dirtyStepState?.isTaskInstructionDirty);
    }
  };

  onChange(target: EventTarget | null): void {
    const value = (target as HTMLTextAreaElement)?.value;
    const monthlyReviewNotesDirtyState = this.isTaskInstructions
      ? !!this.dirtyStepState?.isMonthlyReviewNotesDirty
      : !!value;
    const taskInstructionDirtyState = this.isTaskInstructions
      ? this.taskInstruction?.text !== value
      : !!this.dirtyStepState?.isTaskInstructionDirty;

    this.setNotesDirtyState(monthlyReviewNotesDirtyState, taskInstructionDirtyState);
  }

  onEdit(): void {
    if (
      this.periodCloseService.currentOpenMonth === this.selectedPeriod &&
      this.checklistAdminPermission
    ) {
      this.textarea.enable();
      this.inputComponent?.input?.nativeElement.focus();
    }
  }

  onSave(): void {
    this.pending = true;

    if (this.isTaskInstructions) {
      this.updateTaskInstructuion();

      return;
    }

    this.createMonthlyReviewNote();
  }

  private onTaskInstructionUpdate = ({
    errors,
  }: GraphqlResponse<createNoteMutation> | GraphqlResponse<updateNoteMutation>) => {
    if (errors.length) {
      return;
    }

    this.setNotesDirtyState(!!this.dirtyStepState?.isMonthlyReviewNotesDirty, false);
    this.textarea.disable();
    this.changeDetectorRef.detectChanges();
  };

  get saveButtonTooltip(): string {
    const monthlyReviewNotesTooltip =
      this.textarea.dirty && this.textarea.invalid ? 'Enter Monthly Review Note to Save.' : '';

    return this.isTaskInstructions ? '' : monthlyReviewNotesTooltip;
  }

  get selectedPeriod(): string {
    return this.quarterCloseNotesStore.getValue().selectedPeriod as string;
  }

  private setNotesDirtyState(
    monthlyReviewNotesDirty: boolean,
    taskInstructionDirty: boolean
  ): void {
    const updatedDirtyStep = new DirtyStep(monthlyReviewNotesDirty, taskInstructionDirty);

    this.quarterCloseNotesStore.updateDirtyState(this.workflowId, updatedDirtyStep);
  }

  get taskInstruction(): null | TaskInstruction {
    return this.quarterCloseNotesStore.getValue().notes[this.workflowId]?.taskInstruction;
  }

  get title(): string {
    return this.type === Notes.TaskInstructions ? Notes.TaskInstructions : Notes.MonthlyReviewNotes;
  }

  private updateNote$(
    message: string = this.textarea.value as string
  ): Observable<GraphqlResponse<updateNoteMutation>> {
    return this.gqlService.updateNote$({
      id: (this.taskInstruction as TaskInstruction).id as string,
      message,
    });
  }

  private updateTaskInstructuion(): void {
    const nowDate = new Date();
    const request$ = this.taskInstruction?.isDefault
      ? this.createNote$(NoteType.NOTE_TYPE_TASK_INSTRUCTIONS)
      : this.updateNote$();

    request$
      .pipe(
        tap(({ data, errors }) => {
          if (errors.length) {
            this.overlayService.error(errors);
          } else if (data) {
            this.quarterCloseNotesStore.updateTaskInstruction(this.workflowId, {
              id: data.id,
              text: data.message,
              updated: nowDate.toString(),
            });
          }

          this.pending = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(this.onTaskInstructionUpdate);
  }

  get validators(): ValidatorFn[] {
    return this.isTaskInstructions ? [Validators.required] : [];
  }

  get workflowId(): string {
    return this.workflow$.value?.id || '';
  }
}
